<template>
    <div class="demo-stats-page">
        <!-- Onion-style full-width Tabs wrapper -->
        <div
            class="onion-tabs-wrapper mb-2 mb-sm-3"
            v-if="effectiveRole !== 'basic'"
        >
            <!-- Layer 1: Group (Total / Business* or Total / Shop*) -->
            <ul class="nav nav-tabs onion-layer onion-layer-top flex-wrap mb-1">
                <li class="nav-item" v-for="tab in topTabs" :key="tab">
                    <a
                        href="#"
                        class="nav-link"
                        :class="{ active: activeTopTab === tab }"
                        @click.prevent="
                            activeTopTab = tab;
                            activeModelTab = 'All models';
                        "
                    >
                        {{ tab }}
                    </a>
                </li>
            </ul>

            <!-- Layer 2: Device Model (Total / BTW350 / BT2400P) -->
            <ul class="nav nav-pills onion-layer flex-wrap">
                <li class="nav-item" v-for="tab in modelTabs" :key="tab">
                    <a
                        href="#"
                        class="nav-link"
                        :class="{ active: activeModelTab === tab }"
                        @click.prevent="activeModelTab = tab"
                    >
                        {{ tab }}
                    </a>
                </li>
            </ul>
        </div>

        <div class="content">
            <!-- Current selection summary -->
            <div class="row mb-2" v-if="effectiveRole !== 'basic'">
                <div
                    class="col-12 d-flex justify-content-between align-items-center"
                >
                    <div class="text-muted small mb-1">
                        Showing statistics for
                        <strong>{{ activeTopTab }}</strong>
                        /
                        <strong>{{ activeModelTab }}</strong>
                        <span v-if="effectiveRole">
                            (role: <strong>{{ effectiveRole }}</strong
                            >)
                        </span>
                    </div>
                    <div class="text-muted small text-end">
                        Last updated: <strong>{{ lastUpdateTime }}</strong>
                    </div>
                </div>
                <div class="col-12">
                    <hr class="section-divider" />
                </div>
            </div>
            <!-- User Statistics Block -->
            <div class="row mb-3" v-if="effectiveRole !== 'basic'">
                <div
                    class="col-lg-3 col-md-6 col-sm-12"
                    v-for="card in userCards"
                    :key="card.id"
                >
                    <stats-card>
                        <template #header>
                            <div
                                class="icon-big text-center"
                                :class="`icon-${card.type}`"
                            >
                                <i :class="card.icon"></i>
                            </div>
                        </template>
                        <template #content>
                            <div class="numbers">
                                <p class="card-category">{{ card.title }}</p>
                                <h4 class="card-title">
                                    {{ card.value }}
                                </h4>
                            </div>
                        </template>
                        <template #footer>
                            <hr />
                            <div
                                class="stats d-flex align-items-center justify-content-between"
                            >
                                <div class="trend d-flex align-items-center">
                                    <i
                                        :class="[
                                            'fa',
                                            card.trend.direction === 'up'
                                                ? 'fa-arrow-up text-success'
                                                : 'fa-arrow-down text-danger',
                                        ]"
                                    ></i>
                                    <span class="ml-2 trend-value">
                                        {{ card.trend.value }}
                                    </span>
                                </div>
                                <div class="info-tooltip" :title="card.info">
                                    <i class="fa fa-info-circle"></i>
                                </div>
                            </div>
                        </template>
                    </stats-card>
                </div>
                <div class="col-12">
                    <hr class="section-divider" />
                </div>
            </div>
            <!-- Shop Statistics Block -->
            <div class="row mb-4" v-if="effectiveRole !== 'basic'">
                <div
                    class="col-lg-3 col-md-6 col-sm-12"
                    v-for="card in shopCards"
                    :key="card.id"
                >
                    <stats-card>
                        <template #header>
                            <div
                                class="icon-big text-center"
                                :class="`icon-${card.type}`"
                            >
                                <i :class="card.icon"></i>
                            </div>
                        </template>
                        <template #content>
                            <div class="numbers">
                                <p class="card-category">{{ card.title }}</p>
                                <h4 class="card-title">
                                    {{ card.value }}
                                </h4>
                            </div>
                        </template>
                        <template #footer>
                            <hr />
                            <div
                                class="stats d-flex align-items-center justify-content-between"
                            >
                                <div class="trend d-flex align-items-center">
                                    <i
                                        :class="[
                                            'fa',
                                            card.trend.direction === 'up'
                                                ? 'fa-arrow-up text-success'
                                                : 'fa-arrow-down text-danger',
                                        ]"
                                    ></i>
                                    <span class="ml-2 trend-value">
                                        {{ card.trend.value }}
                                    </span>
                                </div>
                                <div class="info-tooltip" :title="card.info">
                                    <i class="fa fa-info-circle"></i>
                                </div>
                            </div>
                        </template>
                    </stats-card>
                </div>
                <div class="col-12">
                    <hr class="section-divider" />
                </div>
            </div>
            <!-- Battery Test Statistics Block -->
            <div class="row mb-4" v-if="effectiveRole !== 'basic'">
                <div
                    class="col-lg-3 col-md-6 col-sm-12"
                    v-for="card in batteryTestCards"
                    :key="card.id"
                >
                    <stats-card>
                        <template #header>
                            <div
                                class="icon-big text-center"
                                :class="`icon-${card.type}`"
                            >
                                <i :class="card.icon"></i>
                            </div>
                        </template>
                        <template #content>
                            <div class="numbers">
                                <p class="card-category">{{ card.title }}</p>
                                <h4 class="card-title">
                                    {{ card.value }}
                                </h4>
                            </div>
                        </template>
                        <template #footer>
                            <hr />
                            <div
                                class="stats d-flex align-items-center justify-content-between"
                            >
                                <div class="trend d-flex align-items-center">
                                    <i
                                        :class="[
                                            'fa',
                                            card.trend.direction === 'up'
                                                ? 'fa-arrow-up text-success'
                                                : 'fa-arrow-down text-danger',
                                        ]"
                                    ></i>
                                    <span class="ml-2 trend-value">
                                        {{ card.trend.value }}
                                    </span>
                                </div>
                                <div class="info-tooltip" :title="card.info">
                                    <i class="fa fa-info-circle"></i>
                                </div>
                            </div>
                        </template>
                    </stats-card>
                </div>
                <div class="col-12">
                    <hr class="section-divider" />
                </div>
            </div>
            <!-- Charts Row 1: Firmware version + Upload volume -->
            <div
                class="row mt-4"
                v-if="effectiveRole !== 'basic' && hasVersionCharts"
            >
                <!-- Firmware / App versions (place Firmware here; App will flow next row if needed) -->
                <div
                    class="col-lg-6 col-md-6 col-sm-12 mb-4"
                    v-for="chart in versionCharts"
                    :key="chart.id"
                >
                    <ChartCard
                        :title="chart.title"
                        :subTitle="chart.subTitle"
                        :chartData="chart.data"
                        :chartOptions="chart.options"
                        :chartColors="chartColors"
                        :chartType="chart.type"
                        :infoText="chart.info || chart.subTitle"
                    >
                        <template #title>
                            <div
                                class="d-flex justify-content-between align-items-center w-100"
                            >
                                <span>{{ chart.title }}</span>
                                <select
                                    v-model="versionPeriod"
                                    class="form-control form-control-sm active-shops-select"
                                >
                                    <option value="day">Day</option>
                                    <option value="week">Week</option>
                                    <option value="month">Month</option>
                                    <option value="season">Season</option>
                                    <option value="year">Year</option>
                                    <option value="total">Total</option>
                                </select>
                            </div>
                        </template>
                        <template #subTitle>
                            {{ chart.subTitle }}
                            <span v-if="versionPeriodLabel">
                                ({{ versionPeriodLabel }})
                            </span>
                        </template>
                        <template #footer>
                            <i class="fa fa-microchip"></i>
                            Firmware / App versions
                        </template>
                    </ChartCard>
                </div>

                <!-- Yesterday hourly activities -->
                <div
                    class="col-lg-6 col-md-6 col-sm-12 mb-4"
                    v-for="chart in getChartsByGroup('uploadVolume')"
                    :key="chart.id"
                >
                    <ChartCard
                        :title="chart.title"
                        :subTitle="chart.subTitle"
                        :chartData="chart.data"
                        :chartOptions="chart.options"
                        :chartColors="chartColors"
                        :chartType="chart.type"
                        :infoText="chart.info || chart.subTitle"
                    >
                        <template #footer>
                            <i class="fa fa-history"></i>
                            Demo data (randomized per reload)
                        </template>
                    </ChartCard>
                </div>
                <div class="col-12">
                    <hr class="section-divider" />
                </div>
            </div>
            <!-- Charts Row 2: Test result over time (dynamic monthly / quarterly with range, models-only) -->
            <div
                class="row mt-3"
                v-if="
                    effectiveRole !== 'basic' &&
                    !isTotalModelSelected &&
                    testResultTrendChart
                "
            >
                <div class="col-md-12 col-sm-12 mb-4">
                    <ChartCard
                        :title="testResultTrendChart.title"
                        :subTitle="testResultTrendSubTitle"
                        :chartData="testResultTrendChart.data"
                        :chartOptions="testResultTrendChart.options"
                        :chartColors="chartColors"
                        :chartType="testResultTrendChart.type"
                        chartHeight="300px"
                        :infoText="testResultTrendSubTitle"
                    >
                        <template #subTitle>
                            <div
                                class="d-flex flex-wrap justify-content-between align-items-center w-100"
                            >
                                <div class="small text-muted mb-2 mb-sm-0">
                                    {{ testResultTrendSubTitle }}
                                </div>
                                <div
                                    class="d-flex flex-wrap align-items-center"
                                >
                                    <div
                                        class="form-check form-check-inline mb-0 mr-2"
                                    >
                                        <input
                                            class="form-check-input"
                                            type="radio"
                                            id="testResultModeMonthly"
                                            value="monthly"
                                            v-model="testResultMode"
                                        />
                                        <label
                                            class="form-check-label"
                                            for="testResultModeMonthly"
                                        >
                                            Monthly
                                        </label>
                                    </div>
                                    <div
                                        class="form-check form-check-inline mb-0 mr-2"
                                    >
                                        <input
                                            class="form-check-input"
                                            type="radio"
                                            id="testResultModeQuarterly"
                                            value="quarterly"
                                            v-model="testResultMode"
                                        />
                                        <label
                                            class="form-check-label"
                                            for="testResultModeQuarterly"
                                        >
                                            Quarterly
                                        </label>
                                    </div>
                                    <select
                                        v-model.number="testResultRange"
                                        class="form-control form-control-sm"
                                    >
                                        <option
                                            v-for="opt in currentTestResultRangeOptions"
                                            :key="opt.value"
                                            :value="opt.value"
                                        >
                                            {{ opt.label }}
                                        </option>
                                    </select>
                                </div>
                            </div>
                        </template>
                        <template #footer>
                            <i class="fa fa-history"></i>
                            Dynamic test result counts ({{
                                testResultRangeLabel
                            }})
                        </template>
                    </ChartCard>
                </div>
                <div class="col-12">
                    <hr class="section-divider" />
                </div>
            </div>
            <!-- Charts Row 3: Result ratios -->
            <div
                class="row mt-3"
                v-if="effectiveRole !== 'basic' && hasTestResultRatioCharts"
            >
                <div
                    class="col-md-6 col-sm-12 mb-4"
                    v-for="chart in testResultRatioCharts"
                    :key="chart.id"
                >
                    <ChartCard
                        :title="chart.title"
                        :subTitle="chart.subTitle"
                        :chartData="chart.data"
                        :chartOptions="chart.options"
                        :chartColors="chartColors"
                        :chartType="chart.type"
                        chartHeight="320px"
                        :infoText="chart.info || chart.subTitle"
                    >
                        <template #footer>
                            <i class="fa fa-pie-chart"></i>
                            Demo data (current vs total)
                        </template>
                    </ChartCard>
                </div>
                <div class="col-12">
                    <hr class="section-divider" />
                </div>
            </div>
            <!-- Charts Row 4: Active shops & users (combined with period selectors) -->
            <div class="row mt-3">
                <!-- Active Shops -->
                <div
                    class="col-md-6 col-sm-12 mb-4"
                    v-if="effectiveRole !== 'basic'"
                >
                    <ChartCard
                        :title="activeShopsChart && activeShopsChart.title"
                        :subTitle="activeShopsSubTitle"
                        :chartData="
                            activeShopsChart && activeShopsChart.data
                                ? activeShopsChart.data
                                : emptyChartData
                        "
                        :chartOptions="
                            activeShopsChart && activeShopsChart.options
                                ? activeShopsChart.options
                                : {}
                        "
                        :chartColors="chartColors"
                        :chartType="
                            activeShopsChart && activeShopsChart.type
                                ? activeShopsChart.type
                                : 'bar'
                        "
                        :infoText="activeShopsSubTitle"
                    >
                        <template #title>
                            <div
                                class="d-flex justify-content-between align-items-center w-100"
                            >
                                <span>
                                    {{
                                        activeShopsChart
                                            ? activeShopsChart.title
                                            : 'Active Shops'
                                    }}
                                </span>
                                <select
                                    v-model="activeShopsPeriod"
                                    class="form-control form-control-sm active-shops-select"
                                >
                                    <option value="day">Day (yesterday)</option>
                                    <option value="week">Week (7 days)</option>
                                    <option value="month">Month</option>
                                    <option value="quarter">Quarter</option>
                                    <option value="year">Year</option>
                                </select>
                            </div>
                        </template>
                        <template #subTitle>
                            {{ activeShopsSubTitle }}
                        </template>
                        <template #footer>
                            <i class="fa fa-building"></i>
                            <span
                                >Top 5 shops by uploads ({{
                                    activeShopsPeriodLabel
                                }})</span
                            >
                        </template>
                    </ChartCard>
                </div>

                <!-- Active Users -->
                <div
                    :class="
                        effectiveRole === 'basic'
                            ? 'col-md-12 col-sm-12 mb-4'
                            : 'col-md-6 col-sm-12 mb-4'
                    "
                >
                    <ChartCard
                        :title="activeUsersChart && activeUsersChart.title"
                        :subTitle="activeUsersSubTitle"
                        :chartData="
                            activeUsersChart && activeUsersChart.data
                                ? activeUsersChart.data
                                : emptyChartData
                        "
                        :chartOptions="
                            activeUsersChart && activeUsersChart.options
                                ? activeUsersChart.options
                                : {}
                        "
                        :chartColors="chartColors"
                        :chartType="
                            activeUsersChart && activeUsersChart.type
                                ? activeUsersChart.type
                                : 'bar'
                        "
                        :infoText="activeUsersSubTitle"
                    >
                        <template #title>
                            <div
                                class="d-flex justify-content-between align-items-center w-100"
                            >
                                <span>
                                    {{
                                        activeUsersChart
                                            ? activeUsersChart.title
                                            : 'Active Users'
                                    }}
                                </span>
                                <select
                                    v-model="activeUsersPeriod"
                                    class="form-control form-control-sm active-users-select"
                                >
                                    <option value="day">Day (yesterday)</option>
                                    <option value="week">Week (7 days)</option>
                                    <option value="month">Month</option>
                                    <option value="quarter">Quarter</option>
                                    <option value="year">Year</option>
                                </select>
                            </div>
                        </template>
                        <template #subTitle>
                            {{ activeUsersSubTitle }}
                        </template>
                        <template #footer>
                            <i class="fa fa-user"></i>
                            <span
                                >Top 5 users by uploads ({{
                                    activeUsersPeriodLabel
                                }})</span
                            >
                        </template>
                    </ChartCard>
                </div>
                <div class="col-12">
                    <hr class="section-divider" />
                </div>
            </div>
            <!-- Charts Row 5: User growth (total only) -->
            <div class="row mt-3" v-if="effectiveRole !== 'basic'">
                <div
                    class="col-md-12 col-sm-12 mb-4"
                    v-for="chart in getChartsByGroup('usersGrowth')"
                    :key="chart.id"
                >
                    <ChartCard
                        :title="chart.title"
                        :subTitle="chart.subTitle"
                        :chartData="chart.data"
                        :chartOptions="chart.options"
                        :chartColors="chartColors"
                        :chartType="chart.type"
                        chartHeight="300px"
                        :infoText="chart.info || chart.subTitle"
                    >
                        <template #footer>
                            <i class="fa fa-users"></i>
                            New vs total users by month (total only)
                        </template>
                    </ChartCard>
                </div>
                <div class="col-12">
                    <hr class="section-divider" />
                </div>
            </div>
            <!-- Charts Row 8: Test type & battery type -->
            <div
                class="row mt-3"
                v-if="effectiveRole !== 'basic' && hasTestTypeBatteryCharts"
            >
                <div
                    class="col-md-4 col-sm-12 mb-4"
                    v-for="chart in testTypeBatteryCharts"
                    :key="chart.id"
                >
                    <ChartCard
                        :title="chart.title"
                        :subTitle="chart.subTitle"
                        :chartData="chart.data"
                        :chartOptions="chart.options"
                        :chartColors="chartColors"
                        :chartType="chart.type"
                        chartHeight="320px"
                        :infoText="chart.info || chart.subTitle"
                    >
                        <template #footer>
                            <i class="fa fa-battery-half"></i>
                            Test / battery type distribution
                        </template>
                    </ChartCard>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useStore } from 'vuex';
import { ChartCard, StatsCard } from '@/components';
import {
    useDemoCharts,
    randomInt,
    buildTestResultTrendChart,
} from './demoChartsConfig';

export default {
    name: 'DemoStatsAndCharts',
    props: {
        roleView: {
            type: String,
            default: null,
        },
    },
    components: {
        ChartCard,
        StatsCard,
    },
    setup(props) {
        const store = useStore();

        const lastUpdateTime = computed(() => {
            const now = new Date();
            // last update time will be the 00:00:00 yesterday
            now.setDate(now.getDate() - 1);
            now.setHours(0, 0, 0, 0);
            // Format as locale string
            return now.toLocaleString();
        });

        const userSelf = computed(() => store.state.auth.userSelf || {});
        const primaryGroup = computed(
            () => (userSelf.value.groups && userSelf.value.groups[0]) || null
        );

        const normalizedRoleView = computed(() => {
            if (!props.roleView) {
                return null;
            }
            return String(props.roleView).toLowerCase();
        });

        const isSuperAdminByGroup = computed(() => {
            if (!primaryGroup.value) {
                return false;
            }
            return String(primaryGroup.value).toLowerCase() === 'superadmin';
        });

        const effectiveRole = computed(() => {
            if (normalizedRoleView.value) {
                return normalizedRoleView.value;
            }
            if (isSuperAdminByGroup.value) {
                return 'superadmin';
            }
            return 'shop';
        });

        const isSuperAdmin = computed(
            () => effectiveRole.value === 'superadmin'
        );

        const businessTabs = ref([
            'Overall',
            'Business 1',
            'Business 2',
            'Business 3',
        ]);
        const shopTabs = ref(['Business', 'Shop 1', 'Shop 2', 'Shop 3']);
        const singleShopTabs = ref(['This Shop']);
        const modelTabs = ref(['All models', 'BTW350', 'BT2400P']);

        const activeTopTab = ref('Overall');
        const activeModelTab = ref('All models');
        const versionPeriod = ref('total');
        const activeShopsPeriod = ref('day');
        const activeUsersPeriod = ref('day');
        const chartColors = ref([
            '#7a9e9f', // primary
            '#41b883', // success
            '#f3bb45', // warning
            '#68b3c8', // info
            '#eb5e28', // danger
            '#a49e93', // muted
        ]);

        const chartHeightResponsive = ref('160px');

        const updateChartHeight = () => {
            if (window.innerWidth >= 1200) {
                chartHeightResponsive.value = '160px'; // 大螢幕三欄：很扁
            } else if (window.innerWidth >= 768) {
                chartHeightResponsive.value = '180px'; // 中螢幕兩欄
            } else {
                chartHeightResponsive.value = '200px'; // 手機一欄
            }
        };

        onMounted(() => {
            updateChartHeight();
            window.addEventListener('resize', updateChartHeight);
        });

        onBeforeUnmount(() => {
            window.removeEventListener('resize', updateChartHeight);
        });

        const versionPeriodLabel = computed(() => {
            switch (versionPeriod.value) {
                case 'day':
                    return 'Day';
                case 'week':
                    return 'Week';
                case 'month':
                    return 'Month';
                case 'season':
                    return 'Season';
                case 'year':
                    return 'Year';
                case 'total':
                    return 'Total';
                default:
                    return '';
            }
        });

        const topTabs = computed(() => {
            switch (effectiveRole.value) {
                case 'superadmin':
                    return businessTabs.value;
                case 'business':
                    return shopTabs.value;
                case 'shop':
                    return singleShopTabs.value;
                case 'basic':
                    return [];
                default:
                    return businessTabs.value;
            }
        });

        watch(
            effectiveRole,
            newRole => {
                if (newRole === 'superadmin') {
                    activeTopTab.value = 'Overall';
                } else if (newRole === 'business') {
                    activeTopTab.value = 'Business';
                } else if (newRole === 'shop') {
                    activeTopTab.value = 'This Shop';
                } else if (newRole === 'basic') {
                    activeTopTab.value = 'Overall';
                }
            },
            { immediate: true }
        );

        const versionCharts = computed(() => {
            const baseCharts = getChartsByGroup('versions');

            const getRangeForPeriod = period => {
                switch (period) {
                    case 'day':
                        return [20, 80];
                    case 'week':
                        return [80, 220];
                    case 'month':
                        return [150, 360];
                    case 'season':
                        return [260, 520];
                    case 'year':
                        return [500, 1200];
                    case 'total':
                    default:
                        return [800, 1800];
                }
            };

            const [min, max] = getRangeForPeriod(versionPeriod.value);

            return baseCharts.map(chart => {
                const labels = (chart.data && chart.data.labels) || [];
                const sourceDatasets =
                    (chart.data && chart.data.datasets) || [];

                const datasets = sourceDatasets.map((ds, index) => {
                    if (index !== 0) {
                        return ds;
                    }

                    return {
                        ...ds,
                        data: labels.map(() => randomInt(min, max)),
                    };
                });

                return {
                    ...chart,
                    data: {
                        ...(chart.data || {}),
                        datasets,
                    },
                };
            });
        });

        const testResultTimeCharts = computed(() =>
            getChartsByGroup('testResultTime')
        );

        const testResultRatioCharts = computed(() =>
            getChartsByGroup('testResultRatio')
        );

        const testTypeBatteryCharts = computed(() =>
            getChartsByGroup('testTypeBattery')
        );

        const hasVersionCharts = computed(() => versionCharts.value.length > 0);

        const hasTestResultTimeCharts = computed(
            () => testResultTimeCharts.value.length > 0
        );

        const hasTestResultRatioCharts = computed(
            () => testResultRatioCharts.value.length > 0
        );

        const hasTestTypeBatteryCharts = computed(
            () => testTypeBatteryCharts.value.length > 0
        );

        // --- Dynamic test result trend (monthly / quarterly with range) ---
        const testResultMode = ref('monthly'); // 'monthly' | 'quarterly'
        const testResultRange = ref(6); // number of months or quarters

        const monthlyRangeOptions = Array.from(
            { length: 24 - 6 + 1 },
            (_, index) => {
                const value = 6 + index;
                return {
                    value,
                    label: `Last ${value} months`,
                };
            }
        );

        const quarterlyRangeOptions = Array.from(
            { length: 8 - 4 + 1 },
            (_, index) => {
                const value = 4 + index;
                return {
                    value,
                    label: `Last ${value} quarters`,
                };
            }
        );

        const currentTestResultRangeOptions = computed(() =>
            testResultMode.value === 'monthly'
                ? monthlyRangeOptions
                : quarterlyRangeOptions
        );

        const userShopCardDefs = [
            {
                id: 1,
                type: 'success',
                icon: 'fa fa-user-plus',
                title: 'New Users',
                info: 'Number of newly created user accounts in the last 24 hours.',
                valueRange: [80, 180],
                deltaRange: [10, 40],
            },
            {
                id: 2,
                type: 'info',
                icon: 'fa fa-user-circle',
                title: 'Active Users',
                info: 'Users who have uploaded at least one test in the last 24 hours.',
                valueRange: [800, 1800],
                deltaRange: [30, 90],
            },
            {
                id: 3,
                type: 'primary',
                icon: 'fa fa-users',
                title: 'Total Users',
                info: 'Total number of registered user accounts in this tenant.',
                valueRange: [6000, 12000],
                deltaRange: [80, 220],
            },
            {
                id: 4,
                type: 'success',
                icon: 'fa-building-o fa',
                title: 'New Shops',
                info: 'Shops that have been newly onboarded in the last 24 hours.',
                valueRange: [4, 20],
                deltaRange: [2, 8],
            },
            {
                id: 5,
                type: 'warning',
                icon: 'fa fa-building',
                title: 'Active Shops',
                info: 'Shops that have at least one test uploaded in the last 7 days.',
                valueRange: [200, 420],
                deltaRange: [10, 40],
            },
            {
                id: 6,
                type: 'danger',
                icon: 'fa fa-building-o',
                title: 'Total Shops',
                info: 'Total number of shops registered under this tenant.',
                valueRange: [700, 1500],
                deltaRange: [4, 18],
            },
        ];

        const batteryTestCardDefs = [
            {
                id: 101,
                type: 'info',
                icon: 'fa fa-battery-full',
                title: '(2025Q3) Battery Tests',
                info: 'Count of BatteryTest and BatteryTestArchive records in the last quarter.',
                valueRange: [20000, 26000],
                deltaRange: [900, 1800],
            },
            {
                id: 102,
                type: 'primary',
                icon: 'fa fa-briefcase',
                title: '(2025Q3) Active Businesses',
                info: 'Businesses with at least one BatteryTest or BatteryTestArchive record in the last quarter.',
                valueRange: [400, 800],
                deltaRange: [20, 60],
            },
            {
                id: 103,
                type: 'success',
                icon: 'fa fa-user-circle',
                title: '(2025Q3) Active Users',
                info: 'Users with at least one BatteryTest or BatteryTestArchive record in the last quarter.',
                valueRange: [10000, 14000],
                deltaRange: [500, 1200],
            },
            {
                id: 104,
                type: 'warning',
                icon: 'fa fa-tablet',
                title: '(2025Q3) Unique SNs',
                info: 'Unique battery serial numbers tested (BatteryTest + BatteryTestArchive) in the last quarter.',
                valueRange: [3500, 5000],
                deltaRange: [80, 220],
            },
            {
                id: 105,
                type: 'danger',
                icon: 'fa fa-bar-chart',
                title: 'Total Tests',
                info: 'Overall count of all BatteryTest and BatteryTestArchive records.',
                valueRange: [150000, 210000],
                deltaRange: [10000, 22000],
            },
        ];

        // Per-session seeded RNG; we will derive a child RNG per (topTab, modelTab)
        const sessionSeed = Date.now() % 2147483647 || 1;

        const createRng = seed => {
            let state = seed % 2147483647 || 1;
            const next = () => {
                state = (state * 16807) % 2147483647;
                return (state - 1) / 2147483646;
            };
            const nextInt = (min, max) => {
                const low = Math.ceil(min);
                const high = Math.floor(max);
                return Math.floor(next() * (high - low + 1)) + low;
            };
            return { next, nextInt };
        };

        const buildContextSeed = () => {
            const key = `${activeTopTab.value}::${activeModelTab.value}`;
            let hash = 0;
            for (let i = 0; i < key.length; i += 1) {
                hash = (hash * 31 + key.charCodeAt(i)) % 2147483647;
            }
            return (sessionSeed + hash) % 2147483647 || 1;
        };

        const generateUserShopCardsForContext = () => {
            const rng = createRng(buildContextSeed());
            return userShopCardDefs.map(def => {
                const [vMin, vMax] = def.valueRange;
                const [dMin, dMax] = def.deltaRange;
                const valueNum = rng.nextInt(vMin, vMax);
                const deltaNum = rng.nextInt(dMin, dMax);
                return {
                    id: def.id,
                    type: def.type,
                    icon: def.icon,
                    title: def.title,
                    value: valueNum.toLocaleString(),
                    trend: {
                        direction: 'down',
                        value: `-${deltaNum}`,
                    },
                    info: def.info,
                };
            });
        };

        const generateBatteryTestCardsForContext = () => {
            const rng = createRng(buildContextSeed() + 777);
            return batteryTestCardDefs.map(def => {
                const [vMin, vMax] = def.valueRange;
                const [dMin, dMax] = def.deltaRange;
                const valueNum = rng.nextInt(vMin, vMax);
                const deltaNum = rng.nextInt(dMin, dMax);
                return {
                    id: def.id,
                    type: def.type,
                    icon: def.icon,
                    title: def.title,
                    value: valueNum.toLocaleString(),
                    trend: {
                        direction: 'up',
                        value: `+${deltaNum}`,
                    },
                    info: def.info,
                };
            });
        };

        const isTotalModelSelected = computed(
            () => activeModelTab.value === 'All models'
        );

        const emptyChartData = {
            labels: [],
            datasets: [],
        };

        const userShopCards = computed(() => {
            const contextCards = generateUserShopCardsForContext();
            if (isTotalModelSelected.value) {
                // total only
                return contextCards.filter(card =>
                    [
                        'Active Users', // 昨天有上傳測試的 User 數量 (total/every model)
                        'Total Users', // 總共 User 數量 (total only)
                        'New Users', // 昨天新增的 User 數量 (total only)
                        'New Shops', // 昨天新增的 Shop 數量 (total only)
                        'Active Shops', // 昨天有上傳測試的 Shop 數量 (total/every model)
                        'Total Shops', // 總共 Shop 數量 (total only)
                    ].includes(card.title)
                );
            }

            // every model：只保留需要在單一機種也顯示的
            return contextCards.filter(card =>
                [
                    'Active Users', // 昨天有上傳測試的 User 數量
                    'Active Shops', // 昨天有上傳測試的 Shop 數量
                    'Total Tests', // 放在 battery cards 中處理 (這裡不處理)
                ].includes(card.title) === true
                    ? true
                    : [
                          // 額外允許在 model 顯示的，如你之後想開放
                      ].includes(card.title)
            );
        });

        const userCards = computed(() =>
            userShopCards.value.filter(card => card.title.includes('Users'))
        );

        const shopCards = computed(() =>
            userShopCards.value.filter(card => card.title.includes('Shops'))
        );

        const batteryTestCards = computed(() => {
            const contextCards = generateBatteryTestCardsForContext();
            if (isTotalModelSelected.value) {
                // total model: 部分卡片依 role / topTab 顯示
                const baseTitles = [
                    '(2025Q3) Battery Tests', // 前一季 BatteryTest, BatteryTestArchive 數量
                    '(2025Q3) Active Businesses', // 前一季 Business 數量（僅 superadmin + Overall 顯示）
                    '(2025Q3) Active Users', // 前一季 User 數量
                    'Total Tests', // 總測試數量
                ];

                const isOverallContext = activeTopTab.value === 'Overall';

                const allowedTitles =
                    isSuperAdmin.value && isOverallContext
                        ? baseTitles
                        : baseTitles.filter(
                              title => title !== '(2025Q3) Active Businesses'
                          );

                return contextCards.filter(card =>
                    allowedTitles.includes(card.title)
                );
            }

            // every model: SN + 總測試數量
            return contextCards.filter(card =>
                [
                    '(2025Q3) Unique SNs', // sn 數量 (every model only)
                    'Total Tests', // 總測試數量 (total/every model)
                ].includes(card.title)
            );
        });

        const demoChartsHelpers = useDemoCharts(activeModelTab);
        const { charts, getChartsByGroup } = demoChartsHelpers;

        const findChartById = id =>
            charts.value.find(chart => chart.id === id) || null;

        const testResultTrendBaseChart = computed(() => {
            if (testResultMode.value === 'monthly') {
                return findChartById('testResultMonthly');
            }
            return findChartById('testResultQuarterly');
        });

        const testResultTrendChart = computed(() => {
            const base = testResultTrendBaseChart.value;
            if (!base || !base.data) {
                return null;
            }

            const labels = Array.isArray(base.data.labels)
                ? base.data.labels
                : [];

            if (!labels.length) {
                return null;
            }

            const sliceSize = Math.min(
                typeof testResultRange.value === 'number'
                    ? testResultRange.value
                    : 0,
                labels.length
            );

            return buildTestResultTrendChart(base, sliceSize);
        });

        const testResultRangeLabel = computed(() => {
            const unit =
                testResultMode.value === 'monthly' ? 'months' : 'quarters';
            return `Last ${testResultRange.value} ${unit}`;
        });

        const testResultTrendSubTitle = computed(() => {
            const base = testResultTrendBaseChart.value;
            const baseSub = (base && base.subTitle) || 'Result counts';
            const modeLabel =
                testResultMode.value === 'monthly'
                    ? 'per month'
                    : 'per quarter';
            return `${baseSub} (${testResultRangeLabel.value}, ${modeLabel})`;
        });

        const activeShopsChart = computed(() => {
            switch (activeShopsPeriod.value) {
                case 'day':
                    return findChartById('top5ShopsYesterday');
                case 'week':
                    return findChartById('top5Shops7Days');
                case 'month':
                    return findChartById('top5ShopsMonth');
                case 'quarter':
                    return findChartById('top5ShopsQuarter');
                case 'year':
                    return findChartById('top5ShopsYear');
                default:
                    return null;
            }
        });

        const activeShopsSubTitle = computed(() => {
            switch (activeShopsPeriod.value) {
                case 'day':
                    return 'Top 5 shops by uploads (yesterday)';
                case 'week':
                    return 'Top 5 shops by uploads (last 7 days)';
                case 'month':
                    return 'Top 5 shops by uploads (this month)';
                case 'quarter':
                    return 'Top 5 shops by uploads (this quarter)';
                case 'year':
                    return 'Top 5 shops by uploads (this year)';
                default:
                    return '';
            }
        });

        const activeShopsPeriodLabel = computed(() => {
            switch (activeShopsPeriod.value) {
                case 'day':
                    return 'Yesterday';
                case 'week':
                    return 'Last 7 Days';
                case 'month':
                    return 'This Month';
                case 'quarter':
                    return 'This Quarter';
                case 'year':
                    return 'This Year';
                default:
                    return '';
            }
        });

        const activeUsersChart = computed(() => {
            switch (activeUsersPeriod.value) {
                case 'day':
                    return findChartById('top5UsersYesterday');
                case 'week':
                    return findChartById('top5Users7Days');
                case 'month':
                    return findChartById('top5UsersMonth');
                case 'quarter':
                    return findChartById('top5UsersQuarter');
                case 'year':
                    return findChartById('top5UsersYear');
                default:
                    return null;
            }
        });

        const activeUsersSubTitle = computed(() => {
            switch (activeUsersPeriod.value) {
                case 'day':
                    return 'Top 5 users by uploads (yesterday)';
                case 'week':
                    return 'Top 5 users by uploads (last 7 days)';
                case 'month':
                    return 'Top 5 users by uploads (this month)';
                case 'quarter':
                    return 'Top 5 users by uploads (this quarter)';
                case 'year':
                    return 'Top 5 users by uploads (this year)';
                default:
                    return '';
            }
        });

        const activeUsersPeriodLabel = computed(() => {
            switch (activeUsersPeriod.value) {
                case 'day':
                    return 'Yesterday';
                case 'week':
                    return 'Last 7 Days';
                case 'month':
                    return 'This Month';
                case 'quarter':
                    return 'This Quarter';
                case 'year':
                    return 'This Year';
                default:
                    return '';
            }
        });

        watch(
            testResultMode,
            newMode => {
                const options =
                    newMode === 'monthly'
                        ? monthlyRangeOptions
                        : quarterlyRangeOptions;
                const hasCurrent = options.some(
                    opt => opt.value === testResultRange.value
                );
                if (!hasCurrent && options.length > 0) {
                    testResultRange.value = options[0].value;
                }
            },
            { immediate: true }
        );

        return {
            userSelf,
            primaryGroup,
            isSuperAdmin,
            businessTabs,
            shopTabs,
            topTabs,
            modelTabs,
            activeTopTab,
            activeModelTab,
            effectiveRole,
            userShopCards,
            userCards,
            shopCards,
            batteryTestCards,
            charts,
            getChartsByGroup,
            isTotalModelSelected,
            chartColors,
            versionPeriod,
            versionPeriodLabel,
            versionCharts,
            testResultTimeCharts,
            testResultRatioCharts,
            testTypeBatteryCharts,
            hasVersionCharts,
            hasTestResultTimeCharts,
            hasTestResultRatioCharts,
            hasTestTypeBatteryCharts,
            testResultMode,
            testResultRange,
            currentTestResultRangeOptions,
            testResultTrendChart,
            testResultTrendSubTitle,
            testResultRangeLabel,
            activeShopsPeriod,
            activeShopsChart,
            activeShopsSubTitle,
            activeShopsPeriodLabel,
            activeUsersPeriod,
            activeUsersChart,
            activeUsersSubTitle,
            activeUsersPeriodLabel,
            emptyChartData,
            lastUpdateTime,
        };
    },
};
</script>

<style scoped>
.demo-stats-page {
    padding: 0 0 15px 0;
}

.onion-tabs-wrapper {
    width: 100%;
    border-radius: 16px;
    padding: 8px 16px 6px;
    background: linear-gradient(135deg, #ffffff 0%, #f3f5f7 100%);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
    border: 1px solid #e3e3e3;
}

.onion-layer {
    margin-bottom: 4px;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
}

.onion-layer-top {
    border-bottom: 1px solid rgba(0, 0, 0, 0.03);
    padding-bottom: 4px;
}

.onion-layer .nav-link {
    border-radius: 999px;
    margin-right: 6px;
    padding: 4px 14px;
    font-size: 0.8rem;
    border: none;
    color: #6c757d;
    background-color: transparent;
}
.nav-link.active:focus {
    background-color: lightgray !important;
}

/* 上層 Group tabs 的 active 樣式（外圈，較深） */
.onion-layer-top .nav-link.active {
    font-weight: 600;
    color: #2c3e50;
    background: rgba(44, 62, 80, 0.15);
}

/* 下層 Model tabs 的 active 樣式（內圈，較淺） */
.onion-tabs-wrapper .onion-layer:not(.onion-layer-top) .nav-link.active {
    font-weight: 600;
    color: #2c3e50;
    background: rgba(44, 62, 80, 0.08);
}

.demo-stats-page .content {
    padding: 15px 5px 0 5px;
}

.card-title {
    display: flex;
    align-items: baseline;
    gap: 4px;
}

.card-unit {
    font-size: 0.8rem;
    color: #9a9a9a;
}

.trend span {
    font-size: 0.85rem;
}

.info-tooltip {
    cursor: pointer;
    color: #9a9a9a;
}

.info-tooltip:hover {
    color: #666;
}

.tabs-panel {
    border-radius: 8px;
    border: 1px solid #e3e3e3;
    box-shadow: none;
    background: linear-gradient(135deg, #ffffff 0%, #f9fafb 100%);
}

.tabs-panel .nav-tabs {
    border-bottom: 1px solid transparent;
}

.tabs-panel .nav-link {
    border-radius: 14px;
    margin-right: 6px;
    padding: 4px 12px;
    font-size: 0.8rem;
    border: none;
    color: #6c757d;
    background-color: transparent;
}

.tabs-panel .nav-link.active {
    font-weight: 600;
    color: #2c3e50;
    background: rgba(44, 62, 80, 0.06);
}

.tabs-label {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #9a9a9a;
}

.trend-value {
    font-size: 0.78rem;
    padding: 1px 8px;
    border-radius: 999px;
    background: #f4f5f7;
}

.fa-arrow-up.text-success + .trend-value {
    background: rgba(46, 204, 113, 0.08);
    color: #27ae60;
}

.fa-arrow-down.text-danger + .trend-value {
    background: rgba(231, 76, 60, 0.08);
    color: #e74c3c;
}

.section-divider {
    border: 1px solid transparent;
    border-top: 1px solid #333;
    margin: 10px 0 20px;
}
</style>
